import {Alert, Button, Col, Container, FloatingLabel, Form, Modal, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {deleteCourseVideo, getCourseVideoList, upsertCourseVideo} from "../api/CourseApi";
import {toolTipRenderer} from "../utils/gridUtils";
import {dateTimeToStr, getConfLabel, showSuccessToast, titleCase} from "../utils/usacmUtils";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE, CONF_LABEL_KEY_COURSE_PAGE, COURSE_VIDEO_STATUS_NAMES, COURSE_VIDEO_STATUS_READY} from "../constants";

/**
 * Seconds -> h:mm:ss (or m:ss when under an hour)
 */
function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || seconds === '') {
    return '';
  }
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = ('0' + (total % 60)).slice(-2);
  return hours > 0 ? `${hours}:${('0' + minutes).slice(-2)}:${secs}` : `${minutes}:${secs}`;
}

/**
 * The videos in one course, and the dialog to add or edit one.  The videos themselves are uploaded to MUX
 * outside this app - here a video is added by its MUX asset id, and the server reads the playback id, status,
 * duration and thumbnail from MUX each time the video is saved (see courses_api.course_video_upsert()).
 * A page shows a video where its html has {video:key}.  Each video comes with used_on_pages (the titles of those
 * pages), so deleting a video or changing its key warns first - the {video:key} would show as plain text there.
 */
export function CourseVideos({course, canEdit, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [videoList, setVideoList] = useState([]);
  const [errors, setErrors] = useState([]);
  // The video in the add/edit dialog - {} while adding a new one, null when the dialog is closed
  const [editingVideo, setEditingVideo] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  // Add/edit dialog fields
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editVideoKey, setEditVideoKey] = useState('');
  const [editMuxAssetId, setEditMuxAssetId] = useState('');
  const [editErrors, setEditErrors] = useState([]);
  const gridRef = useRef();
  const hasFewRows = (videoList || []).length < 8;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE).toLowerCase();
  const isAdding = !!editingVideo && !editingVideo.id;
  const editKeyChanged = !!editingVideo && !isAdding && editVideoKey.trim() !== editingVideo.video_key;

  function loadVideos() {
    if (!course?.id) {
      return;
    }
    getCourseVideoList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setVideoList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    loadVideos();
  }, [course?.id]);

  /**
   * Opens the edit or delete dialog.  The list is reloaded first so the pages using the key are current - they
   * may have been changed on the Pages tab since this list was loaded.
   */
  function startVideoAction(courseVideo, onFreshVideo) {
    getCourseVideoList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setVideoList(data);
        setErrors([]);
        const freshVideo = data.find(video => video.id === courseVideo.id);
        if (freshVideo) {
          onFreshVideo(freshVideo);
        } else {
          setErrors([{message: 'That video no longer exists.', fields: ['']}]);
        }
      } else {
        setErrors(newErrors);
      }
    });
  }

  function startEditingVideo(courseVideo) {
    setEditTitle(courseVideo.title || '');
    setEditDescription(courseVideo.description || '');
    setEditVideoKey(courseVideo.video_key || '');
    setEditMuxAssetId(courseVideo.mux_asset_id || '');
    setEditErrors([]);
    setEditingVideo(courseVideo);
  }

  function callUpsertVideo() {
    upsertCourseVideo(editingVideo?.id, course.id, editTitle, editDescription, editVideoKey, editMuxAssetId,
      (code, data, newErrors) => {
        if (code === 200) {
          showSuccessToast(isAdding ? 'Added video.' : 'Updated video.');
          setVideoList(data);
          setEditingVideo(null);
          setEditErrors([]);
        } else {
          setEditErrors(newErrors);
        }
      });
  }

  function callDeleteVideo() {
    deleteCourseVideo(deletingVideo?.id, (code, data, newErrors) => {
      setDeletingVideo(null);
      if (code === 200) {
        showSuccessToast('Deleted video.');
        setVideoList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  /**
   * The warning shown when a video's {video:key} is about to stop working on the pages which use it
   */
  function renderKeyInUseWarning(courseVideo, whatHappens) {
    const pages = courseVideo?.used_on_pages || [];
    if (pages.length === 0) {
      return null;
    }
    const variable = <code>{'{video:' + courseVideo.video_key + '}'}</code>;
    return (
      <Alert variant="warning" className="mb-0 mt-3">
        The key {variable} is used on {pages.length === 1 ? `this ${pageLabel}` : `these ${pageLabel}s`}:
        <ul className="mb-2">
          {pages.map((pageTitle, index) => <li key={index}>{pageTitle}</li>)}
        </ul>
        {whatHappens} the {variable} will show as plain text there instead of the video, until a video with that
        key is added again.
      </Alert>
    );
  }

  // Column setup
  const colDefs = [
    {
      field: 'thumbnail_src', headerName: '', sortable: false, maxWidth: 110, minWidth: 110,
      cellRenderer: params => params.value ?
        <img src={params.value} alt="" style={{height: '36px', maxWidth: '90px', objectFit: 'cover'}}/> : null,
    },
    {field: 'title', cellRenderer: toolTipRenderer, headerName: 'Title', flex: 3, minWidth: 150},
    {field: 'video_key', cellRenderer: toolTipRenderer, headerName: 'Key', flex: 2, minWidth: 120},
    {
      field: 'video_status', headerName: 'Status', flex: 1, minWidth: 110,
      valueFormatter: params => COURSE_VIDEO_STATUS_NAMES[params.value] || params.value,
      cellClass: params => params.value === COURSE_VIDEO_STATUS_READY ? '' : 'text-danger',
      headerTooltip: 'The MUX processing status - edit and save the video to check it again',
    },
    {
      field: 'duration', headerName: 'Length', flex: 1, minWidth: 100,
      valueFormatter: params => formatDuration(params.value),
    },
    {field: 'mux_asset_id', cellRenderer: toolTipRenderer, headerName: 'MUX Asset Id', flex: 3, minWidth: 150},
    {
      field: 'used_on_pages', headerName: 'Used On', flex: 1, minWidth: 100,
      headerTooltip: `The number of ${pageLabel}s whose content has this key`,
      valueGetter: params => (params.data.used_on_pages || []).length,
      cellRenderer: params => <span title={(params.data.used_on_pages || []).join(', ')}>{params.value}</span>,
    },
    {
      field: 'create_date', headerName: 'Added', flex: 2, minWidth: 150,
      valueFormatter: params => params.value ? dateTimeToStr(new Date(params.value)) : '',
    },
    ...(canEdit ? [{
      headerName: 'Action',
      sortable: false, filter: false,
      maxWidth: 150, minWidth: 150,
      cellRenderer: params => {
        return <Fragment>
          <Button type="button" size="sm" className="me-2"
                  onClick={() => startVideoAction(params.data, startEditingVideo)}>
            Edit
          </Button>
          <Button type="button" variant="danger" size="sm"
                  onClick={() => startVideoAction(params.data, setDeletingVideo)}>
            Delete
          </Button>
        </Fragment>;
      }
    }] : []),
  ];

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  // A course which has not been saved yet has no id, so there is nowhere to put a video
  if (!course?.id) {
    return (
      <Container fluid className="usacm-container-wide">
        <Row>
          <Col className="text-center mt-3">
            Save this {courseLabel.toLowerCase()} before adding videos.
          </Col>
        </Row>
        <Row>
          <Col className="usacm-button-row mt-3">
            <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="usacm-container-wide">

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={videoList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              rowHeight={46}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          {canEdit &&
            <Button type="button" onClick={() => startEditingVideo({})}>Add Video</Button>
          }
        </Col>
      </Row>

      <Modal show={!!editingVideo}
             onHide={() => setEditingVideo(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isAdding ? 'Add Video' : 'Edit Video'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Row className="mb-3">
            <Col className="col-7">
              <Form.Group controlId="edit_video_title">
                <FloatingLabel controlId="edit_video_title" label="Title">
                  <Form.Control type="text"
                                placeholder="Title"
                                name="title"
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'title')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'title')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="col-5">
              <Form.Group controlId="edit_video_video_key">
                <FloatingLabel controlId="edit_video_video_key" label="Key">
                  <Form.Control type="text"
                                placeholder="Key"
                                name="video_key"
                                value={editVideoKey}
                                onChange={e => setEditVideoKey(e.target.value.toLowerCase())}
                                isInvalid={fieldHasErrors(editErrors, 'video_key')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'video_key')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>
                  Unique in this {courseLabel.toLowerCase()}. Letters, numbers, _ and -.
                  Put <code>{'{video:' + (editVideoKey.trim() || 'key') + '}'}</code> in a {pageLabel} to show it.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group controlId="edit_video_mux_asset_id">
                <FloatingLabel controlId="edit_video_mux_asset_id" label="MUX Asset Id">
                  <Form.Control type="text"
                                placeholder="MUX Asset Id"
                                name="mux_asset_id"
                                value={editMuxAssetId}
                                onChange={e => setEditMuxAssetId(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'mux_asset_id')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'mux_asset_id')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>
                  From the MUX dashboard (the asset id, not the playback id). The status, length and thumbnail
                  are read from MUX every time you save.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Group controlId="edit_video_description">
                <FloatingLabel controlId="edit_video_description" label="Description (optional)">
                  <Form.Control as="textarea"
                                placeholder="Description"
                                name="description"
                                style={{height: '100px'}}
                                value={editDescription}
                                onChange={e => setEditDescription(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'description')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'description')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          {editKeyChanged && renderKeyInUseWarning(editingVideo, 'If you change the key')}

          {getErrorMessageForField(editErrors, '') &&
            <Row>
              <Col className="text-center mt-3 usacm-error-message">
                {getErrorMessageForField(editErrors, '')}
              </Col>
            </Row>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingVideo(null)}>Cancel</Button>
          <Button variant="primary"
                  disabled={!editTitle.trim() || !editVideoKey.trim() || !editMuxAssetId.trim()}
                  onClick={() => callUpsertVideo()}>
            {isAdding ? 'Add Video' : 'Update Video'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deletingVideo}
             onHide={() => setDeletingVideo(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to remove "{deletingVideo?.title}" from this {courseLabel.toLowerCase()}?
            The video itself stays at MUX.
          </p>
          {renderKeyInUseWarning(deletingVideo, 'If you delete it')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingVideo(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => callDeleteVideo()}>Delete Video</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
